import { Box, Paper, Popover } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDesignerMode } from "../../Contexts/DesignerModeContext";
import { getContainerClasses, getPortalParentAttributes, useComponentSelection } from "../../Hooks/useComponentSelection";
import useObservedExpression from "../../Hooks/useObservedExpression";
import { PopoverAnchor } from "../../Models/PopoverAnchor";
import { PopoverContainerModel } from "../../Models/PopoverContainerModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import ComponentFactory from "../ComponentFactory";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";

// Popover positioning configuration
const ANCHOR_ORIGINS: Record<PopoverAnchor, { vertical: "top" | "bottom"; horizontal: "left" | "right" }> = {
  [PopoverAnchor.TopRight]: { vertical: "top", horizontal: "right" },
  [PopoverAnchor.TopLeft]: { vertical: "top", horizontal: "left" },
  [PopoverAnchor.BottomRight]: { vertical: "bottom", horizontal: "right" },
  [PopoverAnchor.BottomLeft]: { vertical: "bottom", horizontal: "left" },
};

const TRANSFORM_ORIGINS: Record<PopoverAnchor, { vertical: "top" | "bottom"; horizontal: "left" | "right" }> = {
  [PopoverAnchor.TopRight]: { vertical: "bottom", horizontal: "right" },
  [PopoverAnchor.TopLeft]: { vertical: "bottom", horizontal: "left" },
  [PopoverAnchor.BottomRight]: { vertical: "top", horizontal: "right" },
  [PopoverAnchor.BottomLeft]: { vertical: "top", horizontal: "left" },
};

const PopoverContainerComponent: React.FC<IComponentProps<PopoverContainerModel>> = ({ component, context, componentPath = [] }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const [visible] = useObservedExpression<boolean | undefined>(component.Visible, context, "PopoverContainerComponent");
  // When Visible evaluates to false in runtime mode, the popover must stay closed
  const isVisible = IsDesignMode || visible !== false;

  const children = component.Children ?? [];
  const triggerChild = children[0] ?? null;
  const popoverChildren = children.slice(1);

  // Clear the pending close timeout
  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  // Schedule close with a small delay so moving from trigger into the popover keeps it open
  const scheduleClose = () => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => setPopoverOpen(false), 120);
  };

  const handleTriggerMouseEnter = () => {
    if (IsDesignMode) return;
    if (!isVisible) return;
    cancelClose();
    setPopoverOpen(true);
  };

  const handleTriggerMouseLeave = () => {
    if (IsDesignMode) return;
    scheduleClose();
  };

  const handlePopoverMouseEnter = () => {
    cancelClose();
  };

  const handlePopoverMouseLeave = () => {
    if (IsDesignMode) return;
    scheduleClose();
  };

  const handleClosePopover = () => {
    setPopoverOpen(false);
  };

  // Clean up timeout on unmount
  useEffect(() => () => cancelClose(), []);

  const componentLayoutSx = getComponentLayout(component);
  const popoverLayoutSx = getComponentLayout({ Layout: component.PopoverLayout } as any);

  const anchorOrigin = ANCHOR_ORIGINS[component.Anchor] ?? ANCHOR_ORIGINS[PopoverAnchor.BottomLeft];
  const transformOrigin = TRANSFORM_ORIGINS[component.Anchor] ?? TRANSFORM_ORIGINS[PopoverAnchor.BottomLeft];

  // Designer mode selection: mark root as container so children can be added
  const outerSelectionProps = useComponentSelection({
    component,
    componentPath,
    isContainer: true,
    isEmpty: children.length === 0,
  });

  const designerMode = useDesignerMode();

  // In designer mode: open popover when this component or any of its descendants is selected;
  // close when selection moves outside this component's subtree (or is cleared).
  // isVisible has no effect in designer mode — the designer can always open the popover.
  useEffect(() => {
    if (!IsDesignMode) return;
    const sp = designerMode?.selectedPath;
    const isThisOrDescendant = !!sp && sp.length >= componentPath.length && componentPath.every((id, i) => sp[i] === id);
    setPopoverOpen(isThisOrDescendant);
  }, [designerMode?.selectedPath, componentPath]);

  // Visual container CSS classes for the popover children area
  const innerContainerClassName = useMemo(() => {
    const isEmpty = popoverChildren.length === 0;
    return getContainerClasses(IsDesignMode, isEmpty);
  }, [popoverChildren.length]);

  const handleComponentClick = useCallback(
    (e: React.MouseEvent) => {
      if (!IsDesignMode) return;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        designerMode?.onSelectComponent?.(component.Id, componentPath);
      }
    },
    [designerMode, component.Id, componentPath]
  );

  return (
    <Box
      ref={anchorRef}
      sx={{
        display: "inline-block",
        ...componentLayoutSx,
        // Padding in designer mode creates selectable area around the trigger child
        // so the PopoverContainer itself can be Ctrl+Clicked without hitting the child
        ...(IsDesignMode ? { p: "6px" } : {}),
      }}
      {...outerSelectionProps}
      // Remove container visual styling from outer box — it belongs on the popover children area
      className={outerSelectionProps.className
        ?.replace(/\biw-container\b/g, "")
        .replace(/\biw-empty\b/g, "")
        .trim()}
      onClick={handleComponentClick}
    >
      {/* First child acts as the hover trigger; Ctrl/Cmd+Click is reserved for designer selection.
          display:inline-block gives the wrapper a real layout box so mouseenter fires even when
          the inner child is a disabled element (pointer-events:none on the child is irrelevant). */}
      {triggerChild && (
        <Box onMouseEnter={handleTriggerMouseEnter} onMouseLeave={handleTriggerMouseLeave} sx={{ display: "inline-block" }}>
          <ComponentFactory parent={component} component={triggerChild} context={context} componentPath={[...componentPath, triggerChild.Id]} />
        </Box>
      )}

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={handleClosePopover}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableRestoreFocus
        disableEnforceFocus
        disableAutoFocus
        hideBackdrop={IsDesignMode}
        disableScrollLock={IsDesignMode}
        keepMounted
        slotProps={{
          root: {
            style: IsDesignMode ? { pointerEvents: "none" } : undefined,
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave,
          },
          paper: {
            sx: {
              minWidth: 200,
              maxHeight: 500,
              overflow: "auto",
              pointerEvents: "auto",
              ...popoverLayoutSx,
            },
          },
        }}
      >
        <Paper elevation={0} sx={{ position: "relative" }} {...getPortalParentAttributes(component.Id, componentPath)}>
          {/* Popover children area — spread outerSelectionProps so the global click handler
              can find data-component-id/data-component-path when walking up the portal DOM */}
          <Box
            {...outerSelectionProps}
            className={innerContainerClassName}
            sx={{
              p: 1,
              minHeight: popoverChildren.length === 0 ? 60 : undefined,
              pointerEvents: "auto",
            }}
            onClick={handleComponentClick}
          >
            {popoverChildren.map((child) => (
              <ComponentFactory key={child.Id} parent={component} component={child} context={context} componentPath={[...componentPath, child.Id]} />
            ))}
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export default PopoverContainerComponent;
